<template>
  <div>
    <div
      v-if="isEspOnShore"
      class="flex flex-column lg:gap-2 lg:flex-row body-2"
    >
      <div
        v-for="option in wellTestPressureInputOptions?.well_test_pressure_input_options"
        :key="`pressure-input-options-${option.value}`"
        class="field-radiobutton"
      >
        <RadioButton
          :disabled="disableRadioButtons"
          :input-id="`pressure-input-options-${option.value}`"
          name="pressure-input-options"
          :value="option.value"
          :model-value="
            wellPropertiesForm.data.value.well_test_pressure_input_type
          "
          @update:model-value="
            wellPropertiesForm.update({
              well_test_pressure_input_type: $event
            })
          "
        />
        <label :for="`pressure-input-options-${option.value}`">
          {{ option.comment }}
        </label>
      </div>
    </div>

    <div class="field-grid field-grid--autofit">
      <ConverterInput
        v-if="
          isEspOnShore &&
          wellPropertiesForm.data.value.well_test_pressure_input_type ===
            Well_Test_Pressure_Input_Options_Enum.Pressure
        "
        label="Datum Point (MD)"
        :disabled="disableFields"
        :base-unit="LengthUnits.Feet"
        :model="LargeLengthUnits.options()"
        :decimal-places="2"
        :rule-names="['datum_casing_length_warning']"
        mode="write"
        :model-value="datumPointFt"
        @update:model-value="emit('update:datum-point-ft', $event)"
      />

      <ConverterInput
        v-if="
          isEspOnShore &&
          wellPropertiesForm.data.value.well_test_pressure_input_type ===
            Well_Test_Pressure_Input_Options_Enum.Pressure
        "
        label="Static Bottomhole Pressure at Datum [Pr]"
        :base-unit="PressureUnits.PoundsPerSquareInchAbsolute"
        :disabled="disableFields"
        :model="PressureUnits.options()"
        :min="0"
        :decimal-places="4"
        mode="write"
        :model-value="staticBottomholePressurePsia"
        @update:model-value="
          emit('update:static-bottomhole-pressure-psia', $event)
        "
      />

      <ConverterInput
        v-if="
          isEspOnShore &&
          wellPropertiesForm.data.value.well_test_pressure_input_type ===
            Well_Test_Pressure_Input_Options_Enum.Pressure &&
          wellPropertiesForm.data.value.well_test_liquid_input_type !==
            'productivity_index_water_cut'
        "
        :disabled="disableFields"
        label="Producing Bottomhole Pressure at Datum [Pwf]"
        :rule-names="['pbhp_greater_or_equal_sbhp']"
        :base-unit="PressureUnits.PoundsPerSquareInchAbsolute"
        :model="PressureUnits.options()"
        :decimal-places="4"
        :min="0"
        mode="write"
        :model-value="producingBottomholePressurePsia"
        @update:model-value="
          emit('update:producing-bottomhole-pressure-psia', $event)
        "
      />

      <ConverterInput
        v-if="!isEspOnShore"
        :disabled="disableFields"
        label="HPS Pump Inlet Pressure [PIP]"
        :base-unit="PressureUnits.PoundsPerSquareInchAbsolute"
        :model="PressureUnits.options()"
        :decimal-places="4"
        :min="0"
        mode="write"
        :model-value="
          wellPropertiesForm.data.value.hps_pump_inlet_pressure_psia
        "
        @update:model-value="
          wellPropertiesForm.update({
            hps_pump_inlet_pressure_psia: $event
          })
        "
      />
      <!-- this new property needs to be used to overide scenarioCalculations.pumpCalculations.aggregate.requiredDischargePressurePSIA.value, -->
      <!--  -->
      <ConverterInput
        v-if="!isEspOnShore"
        :disabled="disableFields"
        label="HPS Required Discharge Pressure [Prd]"
        :base-unit="PressureUnits.PoundsPerSquareInchAbsolute"
        :model="PressureUnits.options()"
        :decimal-places="4"
        :min="0"
        mode="write"
        :model-value="
          wellPropertiesForm.data.value.hps_required_discharge_pressure_psia
        "
        @update:model-value="
          wellPropertiesForm.update({
            hps_required_discharge_pressure_psia: $event
          })
        "
      />

      <ConverterInput
        v-if="!isEspOnShore"
        :disabled="disableFields"
        label="NPSHa"
        :base-unit="LengthUnits.Feet"
        :model="LengthUnits.options()"
        :decimal-places="2"
        :min="0"
        :nullable="true"
        mode="write"
        :model-value="wellPropertiesForm.data.value.npsha_ft"
        @update:model-value="
          wellPropertiesForm.update({
            npsha_ft: $event
          })
        "
      />

      <!-- This will be added back in once we have a better calc for selectedNPSHaFt -->
      <!-- <ConverterInput
        v-if="!isEspOnShore"
        label="NPSHa"
        :base-unit="LengthUnits.Feet"
        :model="LengthUnits.options()"
        mode="convert"
        :model-value="selectedNPSHaFt"
      /> -->

      <template
        v-if="
          wellPropertiesForm.data.value.well_test_pressure_input_type ===
          Well_Test_Pressure_Input_Options_Enum.FluidLevel
        "
      >
        <ConverterInput
          label="Static Fluid Level (depth from surface)"
          :disabled="disableFields"
          :base-unit="LengthUnits.Feet"
          :model="LengthUnits.options()"
          :rule-names="['static_fluid_level_above_surface']"
          :min="0"
          :decimal-places="4"
          mode="write"
          :model-value="staticFluidLevelFeet"
          @update:model-value="emit('update:static-fluid-level-feet', $event)"
        />

        <ConverterInput
          v-if="
            wellPropertiesForm.data.value.well_test_liquid_input_type !==
            'productivity_index_water_cut'
          "
          :disabled="disableFields"
          label="Producing Fluid Level (depth from surface)"
          :rule-names="['producing_fluid_level_above_surface']"
          :base-unit="LengthUnits.Feet"
          :model="LargeLengthUnits.options()"
          :decimal-places="4"
          :min="0"
          mode="write"
          :model-value="producingFluidLevelFeet"
          @update:model-value="
            emit('update:producing-fluid-level-feet', $event)
          "
        />
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { injectStrict } from "@/utils/utils";
import ConverterInput from "@/components/ConverterInput.vue";

import {
  sizingFormKey,
  wellPropertiesFormKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";

import {
  Sizing_Pumping_Type_Enum,
  useWellTestPressureInputOptionsQuery,
  Well_Test_Pressure_Input_Options_Enum
} from "gql/api";
import { LargeLengthUnits, LengthUnits } from "@/libs/units/length";
import { PressureUnits } from "@/libs/units/pressure";
import { computed, watch } from "vue";

const wellPropertiesForm = injectStrict(wellPropertiesFormKey);
const sizingForm = injectStrict(sizingFormKey, null);
const { data: wellTestPressureInputOptions } =
  useWellTestPressureInputOptionsQuery();

// This will be added back in once we have a better calc for selectedNPSHaFt
// const { selectedNPSHaFt } = injectStrict(scenarioCalculationsKey);

const isEspOnShore = computed(() => {
  return (
    sizingForm?.data.value.pumping_type === Sizing_Pumping_Type_Enum.EspOnShore
  );
});

defineProps<{
  disableRadioButtons?: boolean;
  disableFields?: boolean;
  producingBottomholePressurePsia: number;
  staticBottomholePressurePsia: number;
  datumPointFt: number;
  staticFluidLevelFeet: number;
  producingFluidLevelFeet: number;
}>();

const _input_type = computed(
  () => wellPropertiesForm.data.value.well_test_pressure_input_type
);

watch(_input_type, newVal => {
  if (newVal === Well_Test_Pressure_Input_Options_Enum.FluidLevel) {
    emit("forceDatumPointOverride");
  }
});

const emit = defineEmits<{
  (
    event: "update:producing-bottomhole-pressure-psia",
    value: number | null
  ): void;
  (event: "update:static-bottomhole-pressure-psia", value: number | null): void;
  (event: "update:datum-point-ft", value: number | null): void;
  (event: "update:static-fluid-level-feet", value: number | null): void;
  (event: "update:producing-fluid-level-feet", value: number | null): void;
  (event: "forceDatumPointOverride"): void;
}>();
</script>
